import React from 'react';
import { metadataMeta } from '~/lib/utils/meta-utils-client';
import { metadataLoader } from '~/lib/utils/meta-utils-server';

export const loader = metadataLoader((t) => ({
  title: t('common.routes.privacyPolicy.title'),
  description: t('common.routes.privacyPolicy.description'),
}));

export const meta = metadataMeta<typeof loader>();

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <section className="space-y-6">
    <h2 className="text-2xl font-bold">{title}</h2>
    <div className="space-y-2 opacity-70">{children}</div>
  </section>
);

export default function PrivacyPolicyPage() {
  return (
    <main className="max-w-prose space-y-12 p-8 pt-16">
      <header className="space-y-2">
        <h1 className="text-3xl font-bold">ScoreOrNot Privacy Policy</h1>
        <p className="text-sm opacity-50">Last Updated: March 2026</p>
      </header>

      <p>
        Operated by Vivaplay d.o.o., Primorska ulica 13, 1000 Ljubljana,
        Slovenia.
      </p>

      <Section title="1. Who We Are">
        <p>
          Vivaplay d.o.o. (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) owns
          and operates ScoreOrNot.{' '}
          <a
            href="https://scoreornot.com"
            target="_blank"
            rel="noopener noreferrer">
            scoreornot.com
          </a>
          . Registered in Slovenia, Company No. 9839224000, VAT No. SI 29411904.
          Privacy contact:{' '}
          <a href="mailto:privacy@scoreornot.com">privacy@scoreornot.com</a>
          {' | '}
          Vivaplay d.o.o., Primorska ulica 13, 1000 Ljubljana, Slovenia.
        </p>
      </Section>

      <Section title="2. What Personal Data We Collect">
        <ul>
          <li>
            Registration: first and last name, email address, phone number
            (optional), year of birth.
          </li>
          <li>Technical: IP address, device type, browser or app version.</li>
          <li>
            Usage: predictions, scores, participation records, event location
            (when scanning a QR code at a partner event).
          </li>
        </ul>
      </Section>

      <Section title="3. Why We Process Your Data">
        <ul>
          <li>
            Platform operation (account, gameplay, leaderboards) - performance
            of contract, Art. 6(1)(b).
          </li>
          <li>
            Service communications - performance of contract / legitimate
            interests, Art. 6(1)(b)(f).
          </li>
          <li>
            ScoreOrNot marketing - your consent, Art. 6(1)(a). Opt out at any
            time.
          </li>
          <li>
            Analytics and platform improvement - legitimate interests, Art.
            6(1)(f).
          </li>
          <li>
            Sharing data with a partner club - where a ScoreOrNot event is held
            in partnership with a sports club, we may offer you the option to
            share your contact data with that club. This occurs only if you tick
            the clearly labelled, unchecked-by-default, optional consent
            checkbox for that specific club. You may withdraw consent at any
            time in Profile &gt; Privacy &amp; Consents without affecting your
            access to ScoreOrNot. Legal basis: explicit consent, Art. 6(1)(a).
          </li>
          <li>
            Age verification - confirming users are 18 or older per our Terms
            and Conditions.
          </li>
        </ul>
      </Section>

      <Section title="4. Who Receives Your Data">
        <div className="space-y-2">
          <p>We do not sell personal data.</p>
          <p className="font-semibold opacity-100">
            Partner sports clubs (joint controllers)
          </p>
          <p>
            If you give explicit consent via the optional club-specific
            checkbox, we share your name, email, and participation data with the
            named club for their direct marketing and event communications. Each
            such arrangement is governed by a Joint Controller Agreement under
            Article 26 GDPR. Current partner clubs are listed at{' '}
            <a
              href="https://scoreornot.com/partners"
              target="_blank"
              rel="noopener noreferrer">
              scoreornot.com/partners
            </a>
            . You may withdraw consent for any club at any time in Profile &gt;
            Privacy &amp; Consents.
          </p>
          <p className="font-semibold opacity-100">
            Technical service providers (processors)
          </p>
          <p>
            Hosting, analytics, and email delivery providers acting under
            Article 28 GDPR data processing agreements.
          </p>
          <p className="font-semibold opacity-100">Legal authorities</p>
          <p>Where required by law, court order, or supervisory authority.</p>
        </div>
      </Section>

      <Section title="5. Retention">
        <ul>
          <li>
            Account data: retained while your account is active; deleted within
            30 days of a deletion request.
          </li>
          <li>Consent records: retained for up to 5 years after withdrawal.</li>
          <li>Analytics data: anonymised after 24 months.</li>
          <li>
            Data shared with partner clubs: subject to each club&apos;s own
            retention policy from the point of sharing.
          </li>
        </ul>
      </Section>

      <Section title="6. Your Rights">
        <p>
          Access, rectification, erasure, restriction, portability, objection,
          and withdrawal of consent - contact{' '}
          <a href="mailto:privacy@scoreornot.com">privacy@scoreornot.com</a>. We
          respond within 30 days. You may also complain to the Slovenian
          Information Commissioner (Informacijski pooblascenec,{' '}
          <a
            href="https://www.ip-rs.si"
            target="_blank"
            rel="noopener noreferrer">
            www.ip-rs.si
          </a>
          ).
        </p>
      </Section>

      <Section title="7. Security">
        <p>
          We apply appropriate technical and organisational measures. In the
          event of a breach posing risk to your rights, we notify the
          Information Commissioner within 72 hours and affected users without
          undue delay.
        </p>
      </Section>

      <Section title="8. Age Restriction">
        <p>
          ScoreOrNot is for users aged 18 and over. Accounts of underage users
          will be deleted upon discovery.
        </p>
      </Section>

      <Section title="9. Changes">
        <p>
          Material changes communicated by email or in-app notice at least 14
          days before taking effect.
        </p>
      </Section>

      <Section title="10. Contact">
        <p>
          <a href="mailto:privacy@scoreornot.com">privacy@scoreornot.com</a>
          {' | '}
          Vivaplay d.o.o., Primorska ulica 13, 1000 Ljubljana, Slovenia.
        </p>
      </Section>

      <footer>
        <p>© 2026 Vivaplay d.o.o. | scoreornot.com | privacy@scoreornot.com</p>
      </footer>
    </main>
  );
}
